import { useCallback } from 'react'

import { resetAutoFill } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import {
    IDynamicTransportModeMapping,
    ParameterName,
    simpleTransportModes,
    TransportMode,
    TransportModeGroup,
    TransportModeMappingData,
    transportModePerGroupArray,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    LegAutoFillWithUpdate,
    OptionalTransportModeMapping,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'
import { inferAutoFillAndUpdate } from 'views/CalculateEmissions/RFQ/utils/utils'

export const useTransportMode = (
    state: RFQTemplateState,
    transportModeMapping: OptionalTransportModeMapping,
    onUpdate: (mapping: OptionalTransportModeMapping, source: 'inferred' | 'user action') => void,
    autoFill: LegAutoFillWithUpdate['transportMode'],
    selectedGroup: TransportModeGroup | 'Select a column' | undefined,
) => {
    const handleTransportModeChange = useCallback(
        (transportMode: TransportMode | 'Select a column' | undefined) => {
            resetAutoFill(autoFill)

            if (transportMode === undefined) {
                onUpdate(undefined, 'user action')
                return
            }

            function handleAutoFill<T extends ParameterName>(
                parameter: T,
            ): ReturnType<typeof inferAutoFillAndUpdate<T>> {
                return inferAutoFillAndUpdate<T>(state, parameter, autoFill)
            }

            // TODO add departureOn/flightNumber/vesselIMO/vesselName to inference
            const inferenceFields: ParameterName[] = [
                'transportMode',
                'refrigeration',
                'tradelane',
                'transitCorridor',
                'restrictedTransitCorridor',
                'vesselImo',
                'vesselName',
                'aircraftType',
                'flightNumber',
                'departureOn',
                'vehicleType',
                'fuel',
            ]

            const inferenceData = Object.fromEntries(
                inferenceFields.map((e) => [e, handleAutoFill(e)]),
            ) as { [K in (typeof inferenceFields)[number]]: ReturnType<typeof handleAutoFill<K>> }

            const aircraftInference = {
                aircraftType: inferenceData.aircraftType,
                flightNumber: inferenceData.flightNumber,
                departureOn: inferenceData.departureOn,
            }
            const truckInference = {
                fuel: inferenceData.fuel,
                refrigeration: inferenceData.refrigeration,
            }
            // All simple transport modes do not have properties. Build them dynamically
            const simpleTransportModesRecord = simpleTransportModes.reduce(
                (acc, key) => {
                    acc[key] = {}
                    return acc
                },
                {} as { [K in (typeof simpleTransportModes)[number]]: Record<string, unknown> },
            )

            const data: TransportModeMappingData = {
                ...simpleTransportModesRecord,
                'Container Ship': {
                    refrigeration: inferenceData.refrigeration,
                    tradelane: inferenceData.tradelane,
                    vesselImo: inferenceData.vesselImo,
                    vesselName: inferenceData.vesselName,
                    departureOn: inferenceData.departureOn,
                    transitCorridor: inferenceData.transitCorridor,
                    restrictedTransitCorridor: inferenceData.restrictedTransitCorridor,
                    // TODO add arrivalOn to inference
                    arrivalOn: undefined,
                },

                'Passenger Plane': aircraftInference,
                'Cargo Plane': aircraftInference,
                Plane: aircraftInference,

                'Inferred truck': truckInference,
                'Truck rigid 7.5t': truckInference,
                'Truck rigid 12t': truckInference,
                'Truck rigid 20t': truckInference,
                'Truck rigid 26t': truckInference,
                'Truck rigid 32t': truckInference,
                'Truck articulated 34t': truckInference,
                'Truck articulated 40t': truckInference,
                'Truck articulated 44t': truckInference,
                'Truck articulated 60t': truckInference,
                'Truck articulated 72t': truckInference,
                Truck: {
                    ...truckInference,
                    vehicleType: inferenceData.vehicleType,
                },
                'Electric truck': {
                    refrigeration: inferenceData.refrigeration,
                },
                'Electric van': {
                    refrigeration: inferenceData.refrigeration,
                },

                Rail: {},
                'Electric train': {},
                'Diesel train': {},
                'Inland container vessel': {},
            }

            let mode: IDynamicTransportModeMapping['mode']

            if (transportMode === 'Select a column') {
                if (inferenceData.transportMode === undefined) {
                    onUpdate({ mode: undefined }, 'inferred')
                    return
                }
                mode = inferenceData.transportMode
            } else {
                mode = { source: 'constant', value: transportMode }
            }

            onUpdate({ mode, data }, 'inferred')
        },
        [state, onUpdate, autoFill],
    )

    const possibleTransportModeOptions =
        selectedGroup === undefined || selectedGroup === 'Select a column'
            ? undefined
            : transportModePerGroupArray[selectedGroup].join('|')

    return {
        possibleTransportModeOptions,
        handleTransportModeChange,
    }
}
